import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type DeckRecordWire } from '../api-client.js';
import { useAuthStore } from '../auth/auth-store.js';
import { CardPreviewModal } from '../components/CardPreviewModal.js';
import { DeckSidebar } from '../components/DeckSidebar.js';
import { HandCard, type HandCardData } from '../components/HandCard.js';
import { createDeck, deleteDeck } from '../decks/deck-storage.js';
import {
  getDefaultFormat,
  summariseForGame,
  validateForGame,
} from '../decks/formats.js';
import { useDeck } from '../decks/useDeck.js';

type Filter = 'all' | 'player' | 'action' | 'weather' | 'stadium';

export default function DeckBuilderPage() {
  const { gameKey = '', deckId = 'new' } = useParams();
  const navigate = useNavigate();

  const [pool, setPool] = useState<HandCardData[] | null>(null);
  const [poolError, setPoolError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('all');
  const [preview, setPreview] = useState<HandCardData | null>(null);

  // Resolve format up-front so an unknown gameKey fails loudly rather
  // than rendering an empty page.
  let format;
  try {
    format = getDefaultFormat(gameKey);
  } catch (e) {
    return (
      <ErrorPanel message={e instanceof Error ? e.message : String(e)} gameKey={gameKey} />
    );
  }

  const user = useAuthStore((s) => s.user);
  const { deck, loading, saving, error: saveError, setName, add, remove } = useDeck(
    gameKey,
    deckId,
  );
  const [playing, setPlaying] = useState(false);
  const [playError, setPlayError] = useState<string | null>(null);
  // Which deck the AI opponent plays: 'random' (server rolls a legal one),
  // 'mirror' (your current deck), or the id of one of your saved decks.
  const [oppChoice, setOppChoice] = useState<string>('random');
  const [myDecks, setMyDecks] = useState<DeckRecordWire[]>([]);

  useEffect(() => {
    let alive = true;
    api
      .listCards(gameKey)
      .then((data) => {
        if (alive) setPool(data as HandCardData[]);
      })
      .catch((e) => {
        if (alive) setPoolError(e instanceof ApiError ? e.message : String(e));
      });
    return () => {
      alive = false;
    };
  }, [gameKey]);

  // The signed-in user's saved decks — offered as opponents the AI can play.
  useEffect(() => {
    if (!user) return;
    let alive = true;
    api
      .listDecks(gameKey)
      .then((decks) => {
        if (alive) setMyDecks(decks);
      })
      .catch(() => {
        /* non-fatal: the opponent picker just falls back to random/mirror */
      });
    return () => {
      alive = false;
    };
  }, [gameKey, user]);

  // Replace deck-id in the URL once a brand-new deck has been
  // persisted by the first save (the hook updates `deck.id` to the
  // server-issued one).
  useEffect(() => {
    if (deckId === 'new' && deck && deck.id !== '' && !deck.id.startsWith('local_')) {
      // Once the server has minted a real id, swap into the URL so a
      // refresh keeps you on the same draft.
      navigate(`/decks/${gameKey}/${deck.id}`, { replace: true });
    }
  }, [deck, deckId, gameKey, navigate]);

  const filteredPool = useMemo(() => {
    if (!pool) return [];
    if (filter === 'all') return pool;
    return pool.filter((c) => c.kind === filter);
  }, [pool, filter]);

  const summary = useMemo(
    () => summariseForGame(deck?.entries ?? [], (pool ?? []) as never),
    [deck, pool],
  );
  const validation = useMemo(
    () => validateForGame(gameKey, deck?.entries ?? [], (pool ?? []) as never),
    [gameKey, deck, pool],
  );
  // Only LEGAL saved decks may be handed to the AI. Saving is decoupled from
  // legality, so myDecks can include illegal drafts that would fail at play —
  // keep those out of the opponent picker.
  const legalOppDecks = useMemo(
    () =>
      myDecks.filter(
        (d) => d.id !== deck?.id && validateForGame(gameKey, d.entries, (pool ?? []) as never).ok,
      ),
    [myDecks, deck, pool, gameKey],
  );

  async function onDuplicate() {
    if (!deck) return;
    try {
      const copy = await createDeck({
        gameKey: deck.gameKey,
        formatKey: deck.formatKey,
        name: `${deck.name} (copy)`,
        entries: deck.entries,
      });
      navigate(`/decks/${gameKey}/${copy.id}`);
    } catch (e) {
      setPlayError(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function onDelete() {
    if (!deck) return;
    if (!confirm(`Delete "${deck.name}"? This can't be undone.`)) return;
    try {
      await deleteDeck(gameKey, deck.id);
      navigate(`/decks/${gameKey}`);
    } catch (e) {
      setPlayError(e instanceof ApiError ? e.message : String(e));
    }
  }

  async function onPlay() {
    if (!user || !deck) {
      setPlayError('Log in to play.');
      return;
    }
    if (!validation.ok) {
      setPlayError('Deck is not legal — fix the issues before playing.');
      return;
    }
    // Resolve the AI opponent's deck from the picker: a random legal deck
    // (server rolls it), a mirror of your own, or one of your saved decks.
    let botDeck: { entries: Array<{ cardId: string; qty: number }> } | undefined;
    if (oppChoice === 'mirror') {
      botDeck = { entries: deck.entries };
    } else if (oppChoice !== 'random') {
      // Resolve from the LEGAL set only; a now-illegal selection falls back to
      // a random AI deck rather than failing the match server-side.
      const chosen = legalOppDecks.find((d) => d.id === oppChoice);
      if (chosen) botDeck = { entries: chosen.entries };
    }
    setPlayError(null);
    setPlaying(true);
    try {
      const created = await api.createVsBotMatch(
        gameKey,
        user.id,
        { formatKey: deck.formatKey, entries: deck.entries },
        botDeck,
      );
      navigate(`/matches/${created.match.id}`);
    } catch (e) {
      setPlayError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setPlaying(false);
    }
  }

  if (poolError) return <ErrorPanel message={poolError} gameKey={gameKey} />;
  if (loading || !deck) {
    return (
      <p className="text-sm text-ec-bone-dim">Loading deck…</p>
    );
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
        <h2 className="font-display text-lg font-semibold uppercase tracking-wide text-ec-face">Deck builder</h2>
        <span className="font-display text-sm uppercase tracking-wide text-ec-bone-dim">{format.name}</span>
        {saving && (
          <span className="text-xs text-ec-bone-dim" title="Saving to your account">
            Saving…
          </span>
        )}
        <div className="ml-auto flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-xs text-ec-bone-dim">
            <span className="hidden sm:inline">AI plays</span>
            <select
              value={oppChoice}
              onChange={(e) => setOppChoice(e.target.value)}
              className="rounded border border-ec-brass/40 bg-ec-ground px-2 py-1 text-xs text-ec-bone"
              title="Which deck the AI opponent uses"
            >
              <option value="random">Random AI deck</option>
              <option value="mirror">Mirror (your deck)</option>
              {legalOppDecks.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            onClick={onPlay}
            disabled={playing || !validation.ok}
            className="ec-btn ec-btn--gold !py-1.5 text-sm"
            title={
              !validation.ok
                ? 'Deck must be legal to play.'
                : 'Play this deck against the AI.'
            }
          >
            {playing ? 'Starting…' : '▶ Play vs AI'}
          </button>
        </div>
        <Link
          to={`/decks/${gameKey}`}
          className="font-display text-sm uppercase tracking-wide text-ec-brass hover:underline"
        >
          my decks
        </Link>
      </header>
      {(playError || saveError) && (
        <div className="ec-surface p-3 text-sm text-ec-face">
          {playError ?? saveError}
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_320px] lg:gap-6">
        {/* Pool */}
        <section>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            {(['all', 'player', 'action', 'stadium', 'weather'] as Filter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                className={`ec-btn !py-1.5 text-sm ${filter === f ? '!border-ec-brass !text-ec-brass' : ''}`}
              >
                {f}
              </button>
            ))}
            <span className="ml-auto text-xs text-ec-bone-dim">
              {pool ? `${filteredPool.length} of ${pool.length} cards` : 'Loading…'}
            </span>
          </div>

          {!pool ? (
            <p className="text-sm text-ec-bone-dim">Loading card pool…</p>
          ) : (
            <div className="flex flex-wrap gap-3">
              {filteredPool.map((card) => {
                const inDeck = deck.entries.find((e) => e.cardId === card.id)?.qty ?? 0;
                return (
                  <div key={card.id} className="relative">
                    <HandCard card={card} onClick={() => add(card.id ?? '', 1)} />
                    {inDeck > 0 && (
                      <span className="pointer-events-none absolute -right-1 -top-1 rounded-full border-2 border-ec-ground bg-ec-oxblood px-1.5 py-0.5 font-display text-[10px] font-bold tabular-nums text-ec-face shadow">
                        ×{inDeck}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Deck */}
        <div className="lg:sticky lg:top-4 lg:self-start">
          <DeckSidebar
            name={deck.name}
            format={format}
            entries={deck.entries}
            pool={pool ?? []}
            summary={summary}
            validation={validation}
            onRename={setName}
            onIncrement={(id) => add(id, 1)}
            onDecrement={(id) => remove(id)}
            onPreview={(card) => setPreview(card)}
            onDelete={onDelete}
            onDuplicate={onDuplicate}
          />
        </div>
      </div>

      {preview && <CardPreviewModal card={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function ErrorPanel({ message, gameKey }: { message: string; gameKey: string }) {
  return (
    <div className="space-y-3">
      <div className="ec-surface p-3 text-sm text-ec-face">
        {message}
      </div>
      <p className="font-serif text-sm text-ec-bone">
        No deck-builder format is registered for <code className="font-mono">{gameKey}</code>.
      </p>
      <Link to="/" className="inline-block font-display text-sm uppercase tracking-wide text-ec-brass hover:underline">
        ← back to games
      </Link>
    </div>
  );
}
